import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Megaphone, Upload, FileText, Image, Trash2, Download, Eye, Plus, Search, Filter, Link2, QrCode, Share2, Copy, Check, Loader2, X, FolderOpen, BarChart3, TrendingUp, MousePointerClick, IdCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import BusinessCardUploader from '../BusinessCardUploader';
import AdvisorBioEditor from '../AdvisorBioEditor';
import SlugEditor from '../SlugEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Track marketing events
const trackEvent = async (eventType, eventTarget, dealerId) => {
  try {
    await axios.post(`${API}/marketing/track?event_type=${eventType}&event_target=${encodeURIComponent(eventTarget)}&dealer_id=${dealerId || ''}`);
  } catch { /* silent fail */ }
};

const safeCopy = (text) => {
  try {
    navigator.clipboard.writeText(text);
  } catch {
    // Fallback for non-secure contexts
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
};

const MarketingTab = ({ token, isDark, dealer }) => {
  const [activeSection, setActiveSection] = useState('library');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [copiedLink, setCopiedLink] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dealerId, setDealerId] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'brochure' });

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketing/materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(res.data);
      // Get dealer profile for tracking
      try {
        const profile = await axios.get(`${API}/dealer/profile`, { headers: { Authorization: `Bearer ${token}` } });
        setDealerId(profile.data?.id);
      } catch {}
      // Auto-seed if no prebuilt content exists
      if (!res.data.some(m => m.is_prebuilt)) {
        try {
          await axios.post(`${API}/marketing/seed-content`, {}, { headers: { Authorization: `Bearer ${token}` } });
          const res2 = await axios.get(`${API}/marketing/materials`, { headers: { Authorization: `Bearer ${token}` } });
          setMaterials(res2.data);
        } catch {}
      }
    } catch {
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/marketing/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch {
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMaterials(); }, [fetchMaterials]);
  useEffect(() => { if (activeSection === 'analytics') fetchAnalytics(); }, [activeSection, fetchAnalytics]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) { toast.error('Please select a file'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('File must be under 25MB'); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', uploadForm.title || file.name);
    formData.append('description', uploadForm.description);
    formData.append('category', uploadForm.category);

    try {
      const res = await axios.post(`${API}/marketing/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Material uploaded successfully');
      setShowUpload(false);
      setUploadForm({ title: '', description: '', category: 'brochure' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchMaterials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteMaterial = async (id) => {
    try {
      await axios.delete(`${API}/marketing/materials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(prev => prev.filter(m => m.id !== id));
      toast.success('Material deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const copyLink = (text, label) => {
    safeCopy(text);
    setCopiedLink(text);
    toast.success('Copied to clipboard!');
    trackEvent('link_click', label || text, dealerId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const copyTemplate = (text, platform) => {
    safeCopy(text);
    setCopiedLink(text);
    toast.success('Copied to clipboard!');
    trackEvent('template_copy', platform, dealerId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const filteredMaterials = materials.filter(m => {
    const matchSearch = !searchTerm || m.title?.toLowerCase().includes(searchTerm.toLowerCase()) || m.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || m.category === filterType;
    return matchSearch && matchType;
  });

  const cardBg = isDark ? '#141420' : '#fff';
  const border = isDark ? '#1e1e30' : '#e5e7eb';
  const textPrimary = isDark ? '#f0f0f5' : '#111827';
  const textSecondary = isDark ? '#8b8ba3' : '#6b7280';
  const accent = '#3b82f6';

  const categoryColors = {
    brochure: '#6366f1', flyer: '#ec4899', social: '#22c55e', email_template: '#f59e0b',
    video: '#ef4444', presentation: '#0ea5e9', other: '#8b5cf6',
    // AI-generated content categories
    social_post: '#22c55e', email_campaign: '#f59e0b', blog_article: '#3b82f6',
    tips: '#10b981', seasonal: '#f97316', buyer_guide: '#8b5cf6'
  };

  const categoryLabels = {
    brochure: 'Brochure', flyer: 'Flyer', social: 'Social Media', email_template: 'Email Template',
    video: 'Video', presentation: 'Presentation', other: 'Other',
    // AI-generated content categories
    social_post: 'Social Post', email_campaign: 'Email Campaign', blog_article: 'Blog Article',
    tips: 'Tips & Tricks', seasonal: 'Seasonal', buyer_guide: 'Buyer Guide'
  };

  const sections = [
    { id: 'library', label: 'Material Library', icon: FolderOpen },
    { id: 'sharing', label: 'Sharing Tools', icon: Share2 },
    { id: 'card', label: 'Business Card', icon: IdCard },
  ];

  return (
    <div data-testid="marketing-tab" className="px-1">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h2 className={`text-lg sm:text-xl font-extrabold tracking-tight flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Megaphone size={22} className="text-blue-500" />
            Marketing Center
          </h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Upload, manage, and share your marketing materials</p>
        </div>
        <Button
          data-testid="upload-material-btn"
          onClick={() => setShowUpload(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold flex items-center gap-1.5 w-full sm:w-auto justify-center"
        >
          <Plus size={16} /> Upload Material
        </Button>
      </div>

      {/* Section Tabs - Scrollable on mobile */}
      <div className={`flex gap-1 mb-5 p-1 rounded-lg overflow-x-auto ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`} style={{ width: 'fit-content', maxWidth: '100%' }}>
        {sections.map(s => (
          <button
            key={s.id}
            data-testid={`marketing-section-${s.id}`}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
              activeSection === s.id 
                ? `${isDark ? 'bg-gray-700 text-blue-400' : 'bg-white text-blue-600 shadow-sm'}`
                : `${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <s.icon size={14} /> {s.label}
          </button>
        ))}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowUpload(false)} />
          <div data-testid="upload-modal" style={{ position: 'relative', background: cardBg, borderRadius: 16, border: `1px solid ${border}`, padding: 28, width: 460, maxHeight: '90vh', overflow: 'auto', zIndex: 61 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ color: textPrimary, fontSize: 18, fontWeight: 700 }}>Upload Marketing Material</h3>
              <button onClick={() => setShowUpload(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textSecondary }}><X size={20} /></button>
            </div>
            <form onSubmit={handleUpload}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <Label style={{ color: textSecondary, fontSize: 12, display: 'block', marginBottom: 4 }}>File *</Label>
                  <input
                    ref={fileInputRef}
                    data-testid="upload-file-input"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.mp4,.pptx,.docx"
                    style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 13 }}
                  />
                  <p style={{ color: textSecondary, fontSize: 11, marginTop: 4 }}>PDF, images, videos, presentations up to 25MB</p>
                </div>
                <div>
                  <Label style={{ color: textSecondary, fontSize: 12, display: 'block', marginBottom: 4 }}>Title</Label>
                  <Input data-testid="upload-title" value={uploadForm.title} onChange={e => setUploadForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Spring Sale Brochure" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
                </div>
                <div>
                  <Label style={{ color: textSecondary, fontSize: 12, display: 'block', marginBottom: 4 }}>Category</Label>
                  <select data-testid="upload-category" value={uploadForm.category} onChange={e => setUploadForm(f => ({...f, category: e.target.value}))}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14 }}>
                    {Object.entries(categoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <Label style={{ color: textSecondary, fontSize: 12, display: 'block', marginBottom: 4 }}>Description</Label>
                  <textarea data-testid="upload-description" value={uploadForm.description} onChange={e => setUploadForm(f => ({...f, description: e.target.value}))}
                    placeholder="Brief description of this material..." rows={2}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>
              </div>
              <Button data-testid="upload-submit-btn" type="submit" disabled={uploading}
                style={{ width: '100%', marginTop: 18, background: accent, color: '#fff', fontWeight: 600, border: 'none' }}>
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 6 }} /> Uploading...</> : <><Upload size={16} style={{ marginRight: 6 }} /> Upload</>}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Library Section */}
      {activeSection === 'library' && (
        <div data-testid="marketing-library">
          {/* Search & Filter */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: textSecondary }} />
              <Input
                data-testid="marketing-search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search materials..."
                style={{ paddingLeft: 36, background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }}
              />
            </div>
            <select
              data-testid="marketing-filter"
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14 }}
            >
              <option value="all">All Types</option>
              {Object.entries(categoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: accent, margin: '0 auto' }} />
            </div>
          ) : filteredMaterials.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <FolderOpen size={48} style={{ color: textSecondary, opacity: 0.3, margin: '0 auto' }} />
              <h3 style={{ color: textPrimary, marginTop: 16, fontSize: 17, fontWeight: 600 }}>
                {materials.length === 0 ? 'No materials yet' : 'No results found'}
              </h3>
              <p style={{ color: textSecondary, fontSize: 14, marginTop: 6 }}>
                {materials.length === 0 ? 'Upload your first marketing material to get started.' : 'Try adjusting your search or filter.'}
              </p>
              {materials.length === 0 && (
                <Button onClick={() => setShowUpload(true)} style={{ marginTop: 16, background: accent, color: '#fff', border: 'none' }}>
                  <Upload size={16} style={{ marginRight: 6 }} /> Upload First Material
                </Button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {filteredMaterials.map(material => (
                <MaterialCard
                  key={material.id}
                  material={material}
                  onDelete={deleteMaterial}
                  isDark={isDark}
                  cardBg={cardBg}
                  border={border}
                  textPrimary={textPrimary}
                  textSecondary={textSecondary}
                  categoryColors={categoryColors}
                  categoryLabels={categoryLabels}
                  dealerId={dealerId}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sharing Tools Section */}
      {activeSection === 'sharing' && (
        <SharingToolsSection
          isDark={isDark}
          cardBg={cardBg}
          border={border}
          textPrimary={textPrimary}
          textSecondary={textSecondary}
          accent={accent}
          copiedLink={copiedLink}
          onCopy={copyLink}
          onCopyTemplate={copyTemplate}
          dealer={dealer}
          token={token}
        />
      )}

      {/* Business Card Section */}
      {activeSection === 'card' && (
        <BusinessCardSection isDark={isDark} token={token} />
      )}

      {/* Analytics Section */}
      {activeSection === 'analytics' && (
        <AnalyticsSection
          analytics={analytics}
          loading={analyticsLoading}
          onRefresh={fetchAnalytics}
          isDark={isDark}
          cardBg={cardBg}
          border={border}
          textPrimary={textPrimary}
          textSecondary={textSecondary}
          accent={accent}
        />
      )}
    </div>
  );
};

const MaterialCard = ({ material, onDelete, isDark, cardBg, border, textPrimary, textSecondary, categoryColors, categoryLabels, dealerId }) => {
  const color = categoryColors[material.category] || '#8b5cf6';
  const isImage = material.file_type?.startsWith('image/');
  const isTextContent = material.content && !material.file_url;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleView = () => {
    trackEvent('download', material.title || material.id, dealerId);
  };

  const handleCopyContent = () => {
    safeCopy(material.content || material.description || '');
    setCopied(true);
    trackEvent('download', material.title || material.id, dealerId);
    setTimeout(() => setCopied(false), 2000);
  };

  const platformColors = {
    'Facebook / Instagram': '#3b82f6', 'Facebook': '#3b82f6', 'Instagram': '#ec4899',
    'Twitter/X': '#1d9bf0', 'LinkedIn': '#0a66c2', 'Email': '#f59e0b',
    'TikTok / Reels / Shorts': '#000', 'Facebook / LinkedIn': '#3b82f6',
    'Facebook / Reddit': '#3b82f6', 'Google Ads': '#22c55e', 'Facebook Ads': '#3b82f6',
  };
  const pColor = platformColors[material.platform] || textSecondary;

  return (
    <div data-testid={`material-card-${material.id}`} className={`rounded-xl border overflow-hidden transition-all ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Preview */}
      <div className={`relative ${isTextContent ? 'p-3 pt-10' : 'h-32 sm:h-36'} ${isDark ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        {isImage && material.file_url ? (
          <img src={material.file_url} alt={material.title} className="w-full h-full object-cover" />
        ) : isTextContent ? (
          <p className={`text-xs leading-relaxed w-full ${isDark ? 'text-gray-400' : 'text-gray-500'}`} style={{ display: '-webkit-box', WebkitLineClamp: expanded ? 999 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
            {material.content}
          </p>
        ) : (
          <FileText size={36} style={{ color: `${color}40` }} />
        )}
        {/* Category Badge - Top Left */}
        <div className="absolute top-2 left-2 z-10">
          <span className="text-[9px] sm:text-[10px] font-bold px-2 py-1 rounded uppercase shadow-sm text-white" style={{ background: color }}>
            {categoryLabels[material.category] || material.category}
          </span>
        </div>
        {/* Platform Badge - Top Right */}
        {material.platform && (
          <div className="absolute top-2 right-2 z-10">
            <span className="text-[9px] sm:text-[10px] font-semibold px-2 py-1 rounded" style={{ background: `${pColor}18`, color: pColor }}>
              {material.platform}
            </span>
          </div>
        )}
      </div>
      {/* Info */}
      <div className="p-3 sm:p-4">
        <h4 className={`text-sm font-bold mb-1 truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{material.title}</h4>
        {material.description && (
          <p
            className={`text-xs leading-relaxed mb-2 ${expanded ? '' : 'line-clamp-2'} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            style={{ whiteSpace: 'pre-wrap' }}
            data-testid={`material-description-${material.id}`}
          >
            {material.description}
          </p>
        )}
        <div className="flex justify-between items-center">
          <span className={`text-[10px] sm:text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{material.created_at ? new Date(material.created_at).toLocaleDateString() : ''}</span>
          <div className="flex gap-1">
            {(isTextContent || material.description) && (
              <>
                <button
                  onClick={() => setExpanded(!expanded)}
                  className={`p-1.5 rounded ${expanded ? 'bg-blue-500/20 text-blue-500' : isDark ? 'bg-gray-700 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-700'}`}
                  title={expanded ? 'Collapse text' : 'Show full text'}
                  data-testid={`material-expand-${material.id}`}
                >
                  <Eye size={14} />
                </button>
                <button onClick={handleCopyContent} className={`p-1.5 rounded ${copied ? 'bg-green-500/20 text-green-500' : isDark ? 'bg-gray-700 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-700'}`} title="Copy text">
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </>
            )}
            {material.file_url && (
              <a href={material.file_url} target="_blank" rel="noopener noreferrer" onClick={handleView} className={`p-1.5 rounded ${isDark ? 'bg-gray-700 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-700'}`} title="View">
                <Eye size={14} />
              </a>
            )}
            {material.file_url && (
              <a href={material.file_url} download onClick={handleView} className={`p-1.5 rounded ${isDark ? 'bg-gray-700 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-700'}`} title="Download">
                <Download size={14} />
              </a>
            )}
            {!material.is_prebuilt && (
              <button onClick={() => onDelete(material.id)} className={`p-1.5 rounded text-red-500 ${isDark ? 'bg-gray-700 hover:bg-red-500/20' : 'bg-gray-100 hover:bg-red-50'}`} title="Delete">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SharingToolsSection = ({ isDark, cardBg, border, textPrimary, textSecondary, accent, copiedLink, onCopy, onCopyTemplate, dealer, token }) => {
  // Per-dealer share URL — routes buyers to this dealer's landing page so
  // any Ranger chat / quiz lead is attributed to them automatically.
  // We track the current slug in local state so an in-place change via the
  // SlugEditor updates the "Your Quiz Link" card without a full page reload.
  const [liveSlug, setLiveSlug] = React.useState((dealer?.slug || '').trim());
  React.useEffect(() => { setLiveSlug((dealer?.slug || '').trim()); }, [dealer?.slug]);
  const slug = liveSlug;
  const quizLink = slug
    ? `${window.location.origin}/${slug}`
    : `${window.location.origin}/quiz`;

  return (
    <div data-testid="sharing-tools-section" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
      {/* Quiz Link */}
      <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${accent}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Link2 size={20} style={{ color: accent }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700 }}>Your Public Link</h3>
              {slug && (
                <SlugEditor
                  currentSlug={slug}
                  token={token}
                  isDark={isDark}
                  textPrimary={textPrimary}
                  textSecondary={textSecondary}
                  cardBg={cardBg}
                  border={border}
                  accent={accent}
                  onChanged={(next) => setLiveSlug(next)}
                />
              )}
            </div>
            <p style={{ color: textSecondary, fontSize: 12 }}>Share this link so every lead is attributed to you. Old versions of your link auto-redirect.</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input value={quizLink} readOnly style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textSecondary, fontSize: 13 }} />
          <Button data-testid="mkt-copy-quiz-link" onClick={() => onCopy(quizLink, 'quiz_link')}
            style={{ background: copiedLink === quizLink ? '#22c55e' : accent, color: '#fff', border: 'none', flexShrink: 0 }}>
            {copiedLink === quizLink ? <Check size={16} /> : <Copy size={16} />}
          </Button>
        </div>
      </div>

      {/* Social Media Templates */}
      <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#ec489912', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Megaphone size={20} style={{ color: '#ec4899' }} />
          </div>
          <div>
            <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700 }}>Quick Share Templates</h3>
            <p style={{ color: textSecondary, fontSize: 12 }}>Ready-to-use copy for social media & emails</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {[
            { platform: 'Facebook / Instagram', text: `Looking for the perfect RV? Take our free AI-powered quiz and get matched with your dream RV in minutes! 🏕️\n\n${quizLink}`, color: '#3b82f6' },
            { platform: 'Email Subject Line', text: 'Find Your Perfect RV — Free AI Quiz (Takes 2 Minutes)', color: '#f59e0b' },
            { platform: 'Text / SMS', text: `Hey! I found this cool RV matching quiz that helps you find the right one for your lifestyle. Try it: ${quizLink}`, color: '#22c55e' },
          ].map((tmpl, i) => (
            <div key={i} style={{ padding: 14, borderRadius: 10, background: isDark ? '#1a1a28' : '#f9fafb', border: `1px solid ${border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: tmpl.color, fontSize: 12, fontWeight: 600 }}>{tmpl.platform}</span>
                <button
                  data-testid={`copy-template-${i}`}
                  onClick={() => onCopyTemplate(tmpl.text, tmpl.platform)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedLink === tmpl.text ? '#22c55e' : textSecondary, padding: 4 }}
                >
                  {copiedLink === tmpl.text ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
              <p style={{ color: textSecondary, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{tmpl.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AnalyticsSection = ({ analytics, loading, onRefresh, isDark, cardBg, border, textPrimary, textSecondary, accent }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: accent, margin: '0 auto' }} />
        <p style={{ color: textSecondary, marginTop: 12, fontSize: 14 }}>Loading analytics...</p>
      </div>
    );
  }

  const totals = analytics?.totals || { downloads: 0, link_clicks: 0, template_copies: 0, total_events: 0 };
  const daily = analytics?.daily_activity || [];
  const topMaterials = analytics?.top_materials || [];
  const linkBreakdown = analytics?.link_breakdown || {};
  const templateBreakdown = analytics?.template_breakdown || {};

  const maxDaily = Math.max(...daily.map(d => d.downloads + d.link_clicks + d.template_copies), 1);

  return (
    <div data-testid="marketing-analytics">
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard icon={<Download size={20} />} label="Material Downloads" value={totals.downloads} color="#6366f1" isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
        <StatCard icon={<MousePointerClick size={20} />} label="Link Clicks" value={totals.link_clicks} color="#22c55e" isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
        <StatCard icon={<Copy size={20} />} label="Template Copies" value={totals.template_copies} color="#ec4899" isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
        <StatCard icon={<TrendingUp size={20} />} label="Total Events" value={totals.total_events} color="#f59e0b" isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Daily Activity Chart */}
        <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700 }}>Daily Activity (30 days)</h3>
            <button onClick={onRefresh} style={{ background: 'none', border: 'none', cursor: 'pointer', color: accent, fontSize: 12, fontWeight: 600 }}>Refresh</button>
          </div>
          {daily.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <BarChart3 size={36} style={{ color: textSecondary, opacity: 0.3, margin: '0 auto' }} />
              <p style={{ color: textSecondary, marginTop: 12, fontSize: 13 }}>No activity data yet. Start sharing your materials!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 150 }}>
              {daily.slice(-14).map((day, i) => {
                const total = day.downloads + day.link_clicks + day.template_copies;
                const h = Math.max((total / maxDaily) * 130, 4);
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }} title={`${day.date}: ${total} events`}>
                    <div style={{ width: '100%', maxWidth: 24, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                      {day.downloads > 0 && <div style={{ height: (day.downloads / total) * h, background: '#6366f1', borderRadius: '3px 3px 0 0' }} />}
                      {day.link_clicks > 0 && <div style={{ height: (day.link_clicks / total) * h, background: '#22c55e' }} />}
                      {day.template_copies > 0 && <div style={{ height: (day.template_copies / total) * h, background: '#ec4899', borderRadius: '0 0 3px 3px' }} />}
                      {total === 0 && <div style={{ height: 4, background: isDark ? '#1e1e30' : '#e5e7eb', borderRadius: 3 }} />}
                    </div>
                    <span style={{ color: textSecondary, fontSize: 9, marginTop: 4, transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{day.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          )}
          {daily.length > 0 && (
            <div style={{ display: 'flex', gap: 16, marginTop: 14, justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: '#6366f1' }} /><span style={{ color: textSecondary, fontSize: 11 }}>Downloads</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: '#22c55e' }} /><span style={{ color: textSecondary, fontSize: 11 }}>Link Clicks</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: '#ec4899' }} /><span style={{ color: textSecondary, fontSize: 11 }}>Template Copies</span></div>
            </div>
          )}
        </div>

        {/* Top Materials */}
        <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
          <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Top Materials</h3>
          {topMaterials.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <FileText size={36} style={{ color: textSecondary, opacity: 0.3, margin: '0 auto' }} />
              <p style={{ color: textSecondary, marginTop: 12, fontSize: 13 }}>No downloads yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {topMaterials.map((m, i) => {
                const maxCount = topMaterials[0]?.downloads || 1;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: textSecondary, fontSize: 12, fontWeight: 600, width: 20, textAlign: 'right' }}>#{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ color: textPrimary, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{m.name}</span>
                        <span style={{ color: accent, fontSize: 13, fontWeight: 700 }}>{m.downloads}</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: isDark ? '#1a1a28' : '#f4f5f7', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(m.downloads / maxCount) * 100}%`, background: accent, borderRadius: 3, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Link Performance */}
        <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
          <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Link Performance</h3>
          {Object.keys(linkBreakdown).length === 0 ? (
            <p style={{ color: textSecondary, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No link clicks tracked yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(linkBreakdown).map(([link, count]) => (
                <div key={link} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb' }}>
                  <span style={{ color: textPrimary, fontSize: 13, fontWeight: 500 }}>{link.replace('_', ' ')}</span>
                  <span style={{ color: '#22c55e', fontSize: 15, fontWeight: 700 }}>{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Template Performance */}
        <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
          <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Template Performance</h3>
          {Object.keys(templateBreakdown).length === 0 ? (
            <p style={{ color: textSecondary, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No template copies tracked yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(templateBreakdown).map(([tmpl, count]) => (
                <div key={tmpl} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb' }}>
                  <span style={{ color: textPrimary, fontSize: 13, fontWeight: 500 }}>{tmpl}</span>
                  <span style={{ color: '#ec4899', fontSize: 15, fontWeight: 700 }}>{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, color, isDark, cardBg, border, textPrimary, textSecondary }) => (
  <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <div style={{ color, opacity: 0.8 }}>{icon}</div>
      <span style={{ color: textSecondary, fontSize: 12, fontWeight: 500 }}>{label}</span>
    </div>
    <p style={{ color: textPrimary, fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em' }}>{value}</p>
  </div>
);

const BusinessCardSection = ({ isDark, token }) => {
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/dealer/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (active) setDealer(res.data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className={`w-5 h-5 animate-spin ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BusinessCardUploader
        dealer={dealer}
        isDark={isDark}
        onChange={(newUrl) => setDealer(d => ({ ...(d || {}), business_card_url: newUrl }))}
      />
      <BusinessCardUploader
        dealer={dealer}
        isDark={isDark}
        variant="advisor_photo"
        onChange={(newUrl) => setDealer(d => ({ ...(d || {}), advisor_photo_url: newUrl }))}
      />
      <AdvisorBioEditor
        dealer={dealer}
        isDark={isDark}
        onChange={(bio) => setDealer(d => ({ ...(d || {}), advisor_bio: bio }))}
      />
    </div>
  );
};

export default MarketingTab;
